/**
 * AuthService — wraps Firebase Authentication.
 *
 * Provides:
 *  - Reactive `user` / `isAuthenticated` signals
 *  - email/password sign-in, sign-out, forgot/reset password
 *  - Google / Apple social sign-in
 *  - Auto-redirect on auth state change
 *
 * Mirrors the administration portal's AuthService for parity.
 */
import { Injectable, inject, signal, computed, NgZone } from '@angular/core';
import {
  Auth,
  User,
  onAuthStateChanged,
  signOut,
  confirmPasswordReset,
  verifyPasswordResetCode,
  applyActionCode,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  getFirestore,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import {
  FIREBASE_AUTH,
  FIREBASE_APP,
  FIREBASE_FUNCTIONS,
} from '@/app/core/firebase';
import { AnalyticsService } from '@/app/core/analytics';
import { signInWithLegacyFallback } from '@/app/core/auth/legacy-password';
import { PageNavigator } from '@/app/core/auth/page-navigator';
import { routedHttpsCallable } from '@/app/core/api/callable-route';

export type AuthUser = {
  uid: string;
  profileId: string | null;
  memberId: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  providerIds: string[];
  hasPasswordProvider: boolean;
  isOAuthOnly: boolean;
  /**
   * True for the anonymous session the public registration form signs a
   * visitor into so it can authorise a Storage upload before they have any
   * account (registrations-createUploadSlot). See `isAuthenticated` below.
   */
  isAnonymous: boolean;
};

/** Remove every `clubezy_`-prefixed sessionStorage key (e.g. the return URL). */
function clearClubezySessionStorage(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('clubezy_')) sessionStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(FIREBASE_AUTH);
  private firebaseApp = inject(FIREBASE_APP);
  private functions = inject(FIREBASE_FUNCTIONS);
  private navigator = inject(PageNavigator);
  private analytics = inject(AnalyticsService);
  private ngZone = inject(NgZone);
  private authStateVersion = 0;

  // ─── State ─────────────────────────────────────────────────────
  /** The current Firebase user mapped to a simple DTO, or null. */
  readonly user = signal<AuthUser | null>(null);

  /** Whether we've finished the initial auth-state check. */
  readonly ready = signal(false);

  /**
   * A signed-in PERSON — never an anonymous session.
   *
   * The public registration form (elsewhere in this app, via
   * FirebaseService) signs a visitor in anonymously the first time they
   * attach a file, since an unauthenticated Storage write cannot be
   * authorised. Firebase Auth is one instance app-wide, so that anonymous
   * session also flows through THIS service's onAuthStateChanged — and
   * `user() !== null` alone can't tell it apart from a real sign-in. Without
   * this, authGuard let an anonymous upload session into signed-in-only
   * routes, and guestGuard bounced it off the sign-in page as if it were
   * already logged in. Mirrors FirebaseService.isAuthenticated (CLUB-2169).
   */
  readonly isAuthenticated = computed(() => {
    const user = this.user();
    return !!user && !user.isAnonymous;
  });

  /** Any Firebase session at all, including the anonymous upload one. */
  readonly hasSession = computed(() => this.user() !== null);

  constructor() {
    onAuthStateChanged(this.auth, (fbUser) => {
      void this.syncAuthState(fbUser);
    });
  }

  // ─── Actions ───────────────────────────────────────────────────
  async signIn(email: string, password: string): Promise<void> {
    // Transparently supports both post-migration (plaintext) and legacy Meteor
    // (bcrypt-of-sha256hex) passwords, with best-effort self-heal. See
    // legacy-password.ts for the full rationale (CLUB Meteor→Firebase migration).
    // On a genuine self-heal, retire the account's server-side legacy-password
    // flag (fire-and-forget — never blocks or fails sign-in).
    const credential = await signInWithLegacyFallback(
      this.auth,
      email,
      password,
      () => this.clearLegacyPasswordFlag(),
    );
    await this.syncAuthState(credential.user);
    this.analytics.logLogin('password');
  }

  /**
   * Ask the backend whether this email belongs to a migrated account that still
   * needs a password set. Used by the sign-in page to turn a generic
   * invalid-credentials failure into a "set your password" migration notice.
   */
  async getMigrationLoginHint(
    email: string,
  ): Promise<{ status: 'none' | 'passwordSetupRequired' }> {
    const getHint = routedHttpsCallable<
      { email: string },
      { status: 'none' | 'passwordSetupRequired' }
    >(this.functions, 'auth-getMigrationLoginHint');
    const result = await getHint({ email });
    return result.data;
  }

  /**
   * Fire-and-forget: clear the server-side "legacy password" flag after a
   * successful self-heal. Never throws — any failure is swallowed (the account
   * still works; the flag simply lingers until the next heal). Invoked as the
   * `onHealed` callback of signInWithLegacyFallback.
   */
  private clearLegacyPasswordFlag(): void {
    try {
      const clearFlag = routedHttpsCallable(
        this.functions,
        'auth-clearLegacyPasswordFlag',
      );
      void Promise.resolve(clearFlag()).catch((err: unknown) => {
        console.warn('Could not clear legacy password flag:', err);
      });
    } catch (err) {
      console.warn('Could not clear legacy password flag:', err);
    }
  }

  /**
   * Sign out, drop this tab's session keys (the stored post-login return URL,
   * which would otherwise send the next person to sign in here to the previous
   * one's destination), and reload at [redirectTo] so nothing held in memory —
   * prefilled forms, loaded profiles — carries over.
   */
  async signOutUser(redirectTo = '/auth/sign-in'): Promise<void> {
    try {
      await signOut(this.auth);
    } finally {
      clearClubezySessionStorage();
      this.navigator.assign(redirectTo);
    }
  }

  async sendResetEmail(email: string, continueUrl?: string): Promise<void> {
    const resetPassword = routedHttpsCallable(this.functions, 'auth-resetPassword');
    await resetPassword({
      email,
      continueUrl: continueUrl || `${window.location.origin}/auth/sign-in`,
    });
  }

  async confirmReset(code: string, newPassword: string): Promise<void> {
    await confirmPasswordReset(this.auth, code, newPassword);
  }

  async verifyResetCode(code: string): Promise<string> {
    return verifyPasswordResetCode(this.auth, code);
  }

  async verifyEmail(code: string): Promise<void> {
    await applyActionCode(this.auth, code);
  }

  async completeEmailChange(requestId: string): Promise<void> {
    const completeEmailChange = routedHttpsCallable(
      this.functions,
      'auth-completeEmailChange',
    );
    await completeEmailChange({ requestId });
  }

  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(this.auth, provider);
    await this.syncAuthState(credential.user);
  }

  async signInWithApple(): Promise<void> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const credential = await signInWithPopup(this.auth, provider);
    await this.syncAuthState(credential.user);
  }

  /** Direct access to the underlying Auth instance for action-code flows. */
  get firebaseAuth(): Auth {
    return this.auth;
  }

  // ─── Helpers ───────────────────────────────────────────────────
  private async syncAuthState(fbUser: User | null): Promise<void> {
    const version = ++this.authStateVersion;
    const mapped = fbUser ? await this.mapUser(fbUser) : null;

    if (version !== this.authStateVersion) {
      return;
    }

    this.ngZone.run(() => {
      this.user.set(mapped);
      this.ready.set(true);
    });
  }

  private async mapUser(u: User): Promise<AuthUser> {
    const profileId = await this.resolveProfileIdForAuthUid(u.uid);
    const providerIds = u.providerData.map((provider) => provider.providerId);
    const hasPasswordProvider = providerIds.includes('password');
    void this.persistAuthMetadata(
      u,
      profileId,
      providerIds,
      hasPasswordProvider,
    );

    return {
      uid: u.uid,
      profileId,
      memberId: profileId ?? u.uid,
      email: u.email,
      displayName: u.displayName,
      photoURL: u.photoURL,
      providerIds,
      hasPasswordProvider,
      isOAuthOnly: providerIds.length > 0 && !hasPasswordProvider,
      isAnonymous: u.isAnonymous,
    };
  }

  private async persistAuthMetadata(
    user: User,
    profileId: string | null,
    providerIds: string[],
    hasPasswordProvider: boolean,
  ): Promise<void> {
    try {
      const db = getFirestore(this.firebaseApp);
      const authMetadata = {
        email: user.email ?? null,
        primaryEmail: user.email ?? null,
        providerIds,
        hasPasswordProvider,
        isOAuthOnly: providerIds.length > 0 && !hasPasswordProvider,
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', user.uid), authMetadata, { merge: true });
      if (profileId) {
        await setDoc(doc(db, 'profiles', profileId), authMetadata, {
          merge: true,
        });
      }
    } catch (err) {
      console.warn('Could not persist auth provider metadata:', err);
    }
  }

  private async resolveProfileIdForAuthUid(
    uid: string,
    attempts = 1,
  ): Promise<string | null> {
    const db = getFirestore(this.firebaseApp);

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      try {
        const bridgeRef = doc(db, 'users', uid);
        const bridgeSnap = await getDoc(bridgeRef);
        const profileId = bridgeSnap.data()?.['profileId'];
        if (typeof profileId === 'string' && profileId.length > 0) {
          return profileId;
        }
      } catch {
        // Ignore and retry below.
      }

      if (attempt < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 200));
      }
    }

    return null;
  }
}
